import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

// Hooks
import { useModal } from 'components/modals/useModal';

// Util
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

// Components
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import Loader from 'components/Loader';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { DebtReminderSendFormModal, FORM_OVERFLOW_CUSTOM_STYLE } from './DebtReminderSendFormModal';

// Store
import { useLazyGetRelatedDebtInvoicesQuery } from 'store/api/invoices';
import { RootState } from 'store/reducers';

// Types
import { FeatureFlag } from 'types/features.ts';
import {
    InvoiceDataListItem,
    InvoiceDebtReminderDto,
    InvoiceDebtReminderScopeType,
    InvoiceDebtReminderType,
    InvoicePartyRoleTypeCode,
} from 'types/invoices';
import { DebtReminderType } from 'types/tenants.ts';
import { combinePrimaryRecipientEmails, stringToEmailList } from 'utils/emails.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';

interface DebtReminderModalProps {
    onClose: () => void;
    invoiceIds: number[];
    selectedInvoice?: InvoiceDataListItem;
    invoicePdfLanguage?: string;
}

interface DebtReminderForm {
    reminderScope: string;
    reminderType: string;
}

const DebtReminderModal = (props: DebtReminderModalProps) => {
    const { onClose, invoiceIds, selectedInvoice, invoicePdfLanguage } = props;
    const { openModal, closeModal } = useModal();
    const { classifiers, isLoading: classifiersLoading } = useClassifiers({
        codes: ['DEBT_REMINDER_SCOPE_TYPE', 'DEBT_REMINDER_TYPE'],
        autoSort: true,
    });
    const buttonsDisabled = useSelector((state: RootState) => state.buttons.buttonsDisabled);
    const isPrimaryRecipientSkippedIfSpecificProvided = useIsFeatureEnabled(
        FeatureFlag.PRIMARY_RECIPIENT_SKIPPED_IF_SPECIFIC_PROVIDED
    );

    const [fetchData, { isFetching: isLoadingRelatedDebtInvoices }] = useLazyGetRelatedDebtInvoicesQuery();
    const { handleSubmit, control, setValue } = useForm<DebtReminderForm>({
        defaultValues: {
            reminderScope:
                invoiceIds.length === 1
                    ? InvoiceDebtReminderScopeType.SINGLE_INVOICE
                    : InvoiceDebtReminderScopeType.ALL_COMPANY_INVOICES,
            reminderType: InvoiceDebtReminderType.TYPE1,
        },
    });

    const reminderScope = useWatch({
        control,
        name: 'reminderScope',
    });

    const openEmailModal = (invoices: InvoiceDebtReminderDto[], formData: DebtReminderForm) => {
        const { reminderScope, reminderType } = formData;

        if (invoices.length > 0) {
            const invoice = invoices[0];
            const secondaryRecipientMainEmails = invoice.parties.flatMap(({ invoicePartyRoleTypeCode, party }) =>
                invoicePartyRoleTypeCode === InvoicePartyRoleTypeCode.SECONDARY_RECIPIENT && party.mainEmail
                    ? [party.mainEmail]
                    : []
            );
            // If we have a preferred language from the original invoice and only one invoice, apply it
            const invoicesWithLanguage =
                invoicePdfLanguage && invoices.length === 1
                    ? invoices.map((inv) => ({ ...inv, invoicePdfLanguage }))
                    : invoices;

            openModal(
                <DebtReminderSendFormModal
                    onClose={closeModal}
                    invoices={invoicesWithLanguage}
                    debtReminderType={reminderType as InvoiceDebtReminderType}
                    debtReminderScopeType={reminderScope as InvoiceDebtReminderScopeType}
                    debtCollectionReminderIntervals={invoice.debtCollectionReminderIntervals ?? []}
                    initialData={{
                        to: combinePrimaryRecipientEmails(
                            invoice?.primaryRecipientParty?.mainEmail,
                            invoice?.primaryRecipientAdditionalEmails,
                            isPrimaryRecipientSkippedIfSpecificProvided
                        ),
                        cc: secondaryRecipientMainEmails.length
                            ? secondaryRecipientMainEmails.concat(
                                  stringToEmailList(invoice?.secondaryRecipientAdditionalEmails)
                              )
                            : stringToEmailList(invoice?.secondaryRecipientAdditionalEmails),
                        bcc: stringToEmailList(invoice?.invoiceArchiveEmails),
                        selectedInvoices: invoices.map((invoice) => invoice.id),
                        replyTo: [],
                    }}
                />,
                closeModal,
                FORM_OVERFLOW_CUSTOM_STYLE
            );
        }
    };

    const onSubmit = async (data: DebtReminderForm) => {
        const { reminderScope, reminderType } = data;
        const relatedDebtInvoicesData = await fetchData({
            invoiceId: invoiceIds[0],
            scopeType: reminderScope as InvoiceDebtReminderScopeType,
            debtReminderType: reminderType,
        });

        if (relatedDebtInvoicesData?.data) {
            onClose();
            openEmailModal(relatedDebtInvoicesData.data, data);
        }
    };

    if (classifiersLoading || isLoadingRelatedDebtInvoices) {
        return <Loader />;
    }

    return (
        <>
            <ModalHeader title={t('invoices.debt-reminder-modal.title')} onCancel={onClose} />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                <div className="flex flex-col gap-4">
                    <RadioInput
                        setValue={setValue}
                        control={control}
                        row={true}
                        label={'invoices.debt-reminder-modal.reminder-scope'}
                        name="reminderScope"
                        options={
                            (getValidOptionsFromClassifier(classifiers['DEBT_REMINDER_SCOPE_TYPE'], false) as {
                                label: string;
                                value: string;
                            }[]) ?? []
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        getOptionDisabled={(option) => option.value === 'SINGLE_INVOICE' && invoiceIds.length > 1}
                    />

                    {reminderScope === InvoiceDebtReminderScopeType.ALL_COMPANY_INVOICES && (
                        <div>
                            <p className="text-body2 text-fg-secondary mb-1">{t('company')}</p>
                            <p className="text-body1">{selectedInvoice?.invoicedByOfficeCompany || '-'}</p>
                        </div>
                    )}

                    <RadioInput
                        setValue={setValue}
                        control={control}
                        row={true}
                        label={'invoices.debt-reminder-modal.reminder-type'}
                        name="reminderType"
                        options={
                            (
                                getValidOptionsFromClassifier(classifiers['DEBT_REMINDER_TYPE'], false) as {
                                    label: string;
                                    value: string;
                                }[]
                            ).filter((type) => type.value !== DebtReminderType.REMINDER_GRACE_PERIOD) ?? []
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                    />
                </div>
            </div>
            <ModalFooterSimple
                buttons={[
                    <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                        {t('cancel')}
                    </Button>,
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        key="debt-reminder-send"
                        onClick={() => {
                            void handleSubmit(onSubmit)();
                        }}
                        disabled={buttonsDisabled}
                    >
                        {t('continue')}
                    </Button>,
                ]}
            />
        </>
    );
};

export const useDebtReminderModal = (
    invoiceIds: number[] | undefined,
    selectedInvoice?: InvoiceDataListItem,
    invoicePdfLanguage?: string
) => {
    const { openModal, closeModal } = useModal();

    const openDebtReminderModal = async () => {
        if (Array.isArray(invoiceIds) && invoiceIds.length > 0) {
            openModal(
                <DebtReminderModal
                    onClose={closeModal}
                    invoiceIds={invoiceIds}
                    selectedInvoice={selectedInvoice}
                    invoicePdfLanguage={invoicePdfLanguage}
                />,
                closeModal
            );
        }
    };

    return {
        openDebtReminderModal,
    };
};
